/**
 * Every read and write the Connections tab makes, in one hook: the connected
 * services, the agent identity, the custom drafts, connector health, the
 * early-access requests, the repository count, and the OAuth return notices
 * each connector's callback redirects back with. The request code is the one
 * the Connections page has always run; only the chrome around it changed.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import type { ConnectedService, ConnectedServiceType, ConnectorDraft } from "@yagni/shared";
import { getConnectedServiceDefinition } from "@yagni/shared";
import { captureUiError } from "../sentry.js";
import type { WorkspaceAgentIdentity } from "../lib/agentIdentity.js";
import { decodeJiraSites, type JiraSiteChoice } from "../lib/oauthInstallUrls.js";
import { GITHUB_CANCELLED_MESSAGE, githubErrorMessage } from "../lib/githubInstallOutcome.js";
import { buildConnectorRecords, type ConnectorStatusRow } from "./connectorsModel.js";

export interface ConnectionNotice {
  key: string;
  tone: "info" | "error";
  message: string;
  /** The Atlassian sites to pick from after `jiraError=choose_site`. */
  jiraSites?: JiraSiteChoice[];
}

export function jiraConnectMessage(errorCode: string | null, cancelled: boolean, hasSites: boolean): string {
  switch (errorCode) {
    case "choose_site":
      return hasSites
        ? "Your Atlassian account reaches more than one Jira site. Pick the one this workspace works in."
        : "Your Atlassian account reaches more than one Jira site. Click Connect to pick one.";
    case "admin_required":
      return "Only a workspace admin can connect Jira.";
    case "personal_requires_workspace":
      return "Connect the workspace Jira account before linking your own.";
    case "site_mismatch":
      return "Your Jira site doesn't match this workspace's site.";
    case "no_accessible_site":
      return "Your Atlassian account can't reach any Jira Cloud site. Check that you can open Jira with it, then connect again.";
    case "accessible_resources_failed":
      return "Atlassian didn't list your Jira sites just now. Try connecting again.";
    case "token_exchange_failed":
      return "Atlassian didn't finish the sign-in. Try connecting again.";
    default:
      return cancelled
        ? "Jira connection was cancelled before it finished. Click Connect to try again."
        : "Jira connection didn't complete. Please try reconnecting.";
  }
}

/** Drop OAuth-return params from the address without a navigation. */
function cleanParams(names: string[]): URLSearchParams {
  const params = new URLSearchParams(window.location.search);
  for (const name of names) params.delete(name);
  const clean = params.toString();
  window.history.replaceState({}, "", window.location.pathname + (clean ? `?${clean}` : ""));
  return params;
}

async function errorFrom(response: Response, fallback: string): Promise<Error> {
  const body = (await response.json().catch(() => ({}))) as { error?: string };
  return new Error(body.error ?? fallback);
}

export interface ServiceEdits {
  capabilities: string[];
  credentials: Record<string, string>;
  webhookSecret: string;
}

export function blankCredentials(serviceType: ConnectedServiceType): Record<string, string> {
  return Object.fromEntries(getConnectedServiceDefinition(serviceType).credentialFields.map((field) => [field.key, ""]));
}

export function filledCredentials(credentials: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(credentials).filter((entry) => entry[1].trim().length > 0));
}

export function useConnections(refreshKey: number) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [services, setServices] = useState<ConnectedService[]>([]);
  const [agents, setAgents] = useState<WorkspaceAgentIdentity[]>([]);
  const [drafts, setDrafts] = useState<ConnectorDraft[]>([]);
  const [statusRows, setStatusRows] = useState<ConnectorStatusRow[]>([]);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notices, setNotices] = useState<ConnectionNotice[]>([]);
  const [githubPendingInstall, setGithubPendingInstall] = useState<{ installationId: string; account: string } | null>(null);
  const [githubPendingBusy, setGithubPendingBusy] = useState(false);
  const [repoPickerOpen, setRepoPickerOpen] = useState(false);
  const [repoCount, setRepoCount] = useState<number | null>(null);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [retried, setRetried] = useState<Record<string, boolean>>({});
  const [requested, setRequested] = useState<Record<string, boolean>>({});
  const [freeformDone, setFreeformDone] = useState<string | null>(null);

  const report = useCallback(
    (err: unknown, operation: string) => captureUiError(err, { area: "connectors", operation, route: location.pathname }),
    [location.pathname],
  );
  const fail = (err: unknown) => setError(err instanceof Error ? err.message : "Unknown error");
  const notice = (next: ConnectionNotice) => setNotices((current) => [...current.filter((n) => n.key !== next.key), next]);
  const setBusyFor = (key: string, value: boolean) => setBusy((current) => ({ ...current, [key]: value }));

  const loadStatus = useCallback(async () => {
    try {
      const response = await fetch("/api/settings/connector-status", { credentials: "include" });
      if (!response.ok) throw new Error("Failed to load connector health");
      setStatusRows((await response.json()) as ConnectorStatusRow[]);
      setStatusError(null);
    } catch (err) {
      setStatusError(err instanceof Error ? err.message : "Unknown error");
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [servicesResponse, agentsResponse, draftsResponse] = await Promise.all([
        fetch("/api/connected-services"),
        fetch("/api/settings/agent-identity"),
        fetch("/api/connector-drafts"),
      ]);
      if (!servicesResponse.ok) throw new Error("Failed to load connected services");
      if (!agentsResponse.ok) throw new Error("Failed to load the agent identity");
      const [servicesData, identity] = (await Promise.all([servicesResponse.json(), agentsResponse.json()])) as [
        ConnectedService[],
        WorkspaceAgentIdentity,
      ];
      setServices(servicesData);
      setAgents([identity]);
      if (draftsResponse.ok) setDrafts((await draftsResponse.json()) as ConnectorDraft[]);
      else {
        // The services still render; a draft waiting on its key must not vanish silently.
        setDrafts([]);
        setError("Custom connection drafts did not load. Refresh to try again.");
        captureUiError(new Error(`connector drafts read failed (${draftsResponse.status})`), {
          area: "connectors",
          operation: "load_connector_drafts",
          route: window.location.pathname,
        });
      }
    } catch (err) {
      fail(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRepoCount = useCallback(() => {
    fetch("/api/repos", { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error(`repos read failed (${res.status})`);
        return res.json() as Promise<unknown[]>;
      })
      .then((repos) => setRepoCount(repos.length))
      .catch((err: unknown) => {
        // Unknown, not zero: the GitHub line then says nothing about the count.
        console.warn("[connections] repository count unavailable", err);
        setRepoCount(null);
      });
  }, []);

  const loadRequests = useCallback(async () => {
    try {
      const response = await fetch("/api/connected-services/requests", { credentials: "include" });
      if (!response.ok) return;
      const data = (await response.json()) as { requests?: Array<{ connectorId: string }> };
      if (!data || !Array.isArray(data.requests)) return;
      setRequested(Object.fromEntries(data.requests.map((r) => [r.connectorId, true])));
    } catch {
      // The Requested marks are a courtesy; the page reads fine without them.
    }
  }, []);

  const reloadAll = useCallback(() => {
    loadData().catch((err) => report(err, "load_connected_services"));
    void loadStatus();
  }, [loadData, loadStatus, report]);

  useEffect(() => {
    reloadAll();
    loadRepoCount();
    void loadRequests();
  }, [reloadAll, loadRepoCount, loadRequests, refreshKey]);

  // A "Queued" retry mark clears once the source reads healthy again.
  useEffect(() => {
    setRetried((current) => {
      const recovered = statusRows.filter((row) => current[row.source] && row.state === "healthy");
      if (recovered.length === 0) return current;
      const next = { ...current };
      for (const row of recovered) delete next[row.source];
      return next;
    });
  }, [statusRows]);

  // The OAuth returns. Each callback redirects back with its own params;
  // every outcome either reloads (it connected) or says why it did not, and
  // the params leave the address so a reload does not repeat the notice.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("workspaceConnected")) {
      reloadAll();
      cleanParams(["workspaceConnected"]);
    }
    if (params.has("githubConnected")) {
      setRepoPickerOpen(true);
      cleanParams(["githubConnected"]);
    }
    const oauthReturn = (
      name: "stripe" | "notion" | "linear",
      message: (errorCode: string | null, cancelled: boolean) => string,
    ) => {
      const connected = params.has(`${name}Connected`);
      const errorCode = params.get(`${name}Error`);
      const cancelled = params.has(`${name}Cancelled`);
      if (!connected && !errorCode && !cancelled) return;
      if (connected) reloadAll();
      else notice({ key: name, tone: "error", message: message(errorCode, cancelled) });
      cleanParams([`${name}Connected`, `${name}Error`, `${name}Cancelled`]);
    };
    oauthReturn("stripe", (errorCode, cancelled) =>
      errorCode === "not_configured"
        ? 'Stripe app installs aren\'t available in this environment yet. Use "Use API key" on Stripe instead.'
        : cancelled
          ? "Stripe connection was cancelled before it finished. Click Connect to try again."
          : "Stripe connection didn't complete. Please try reconnecting.",
    );
    oauthReturn("notion", (errorCode, cancelled) =>
      errorCode === "oauth_not_configured"
        ? "Notion isn't configured on our side yet. We've been notified. Please try again shortly."
        : cancelled
          ? "Notion connection was cancelled before it finished. Click Connect to try again."
          : "Notion connection didn't complete. Please try reconnecting.",
    );
    oauthReturn("linear", (errorCode, cancelled) =>
      errorCode === "personal_requires_workspace"
        ? "Connect the workspace Linear account before linking your own."
        : errorCode === "org_mismatch"
          ? "Your Linear account belongs to a different organization than this workspace."
          : cancelled
            ? "Linear connection was cancelled before it finished. Click Connect to try again."
            : "Linear connection didn't complete. Please try reconnecting.",
    );

    const jiraConnected = params.has("jiraConnected");
    const jiraError = params.get("jiraError");
    const jiraCancelled = params.has("jiraCancelled");
    if (jiraConnected || jiraError || jiraCancelled) {
      if (jiraConnected) reloadAll();
      else {
        const sites = jiraError === "choose_site" ? decodeJiraSites(params.get("jiraSites")) : [];
        notice({
          key: "jira",
          tone: sites.length > 0 ? "info" : "error",
          message: jiraConnectMessage(jiraError, jiraCancelled, sites.length > 0),
          jiraSites: sites,
        });
      }
      cleanParams(["jiraConnected", "jiraError", "jiraCancelled", "jiraSites"]);
    }

    const requestedOnGithub = params.has("githubRequested");
    const cancelledOnGithub = params.has("githubCancelled");
    if (requestedOnGithub || cancelledOnGithub) {
      notice({
        key: "github",
        tone: "info",
        message: requestedOnGithub
          ? "GitHub sent your request to an owner of your GitHub organization. When they approve it, GitHub brings them back here to finish connecting. If YAGNI is already installed on GitHub, use Link existing."
          : GITHUB_CANCELLED_MESSAGE,
      });
      cleanParams(["githubRequested", "githubCancelled"]);
    }

    const installationId = params.get("githubPendingInstall");
    if (installationId && /^\d{1,20}$/.test(installationId)) {
      setGithubPendingInstall({ installationId, account: params.get("githubPendingAccount") ?? "your GitHub account" });
      cleanParams(["githubPendingInstall", "githubPendingAccount"]);
    }
    // Once per arrival: the params are read off the address and cleaned.
  }, [location.pathname, location.search]);

  const githubError = searchParams.get("githubError");
  useEffect(() => {
    if (!githubError) return;
    notice({ key: "github", tone: "error", message: githubErrorMessage(githubError) });
    cleanParams(["githubError"]);
  }, [githubError]);

  const confirmGithubPendingInstall = useCallback(async () => {
    if (!githubPendingInstall || githubPendingBusy) return;
    setGithubPendingBusy(true);
    const failed = (message: string) => {
      notice({ key: "github", tone: "error", message });
      setGithubPendingBusy(false);
    };
    try {
      const response = await fetch("/api/auth/github/pending-install", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ installationId: githubPendingInstall.installationId, returnTo: "/services" }),
      });
      const payload = (await response.json().catch(() => null)) as { authorizeUrl?: unknown; error?: unknown } | null;
      if (!response.ok || typeof payload?.authorizeUrl !== "string") {
        report(new Error(`github pending-install failed (${response.status})`), "github_pending_install");
        failed(typeof payload?.error === "string" ? payload.error : "Could not start the GitHub confirmation. Try Link existing instead.");
        return;
      }
      window.location.assign(payload.authorizeUrl);
    } catch (err) {
      report(err, "github_pending_install");
      failed("Could not start the GitHub confirmation. Try Link existing instead.");
    }
  }, [githubPendingInstall, githubPendingBusy, report]);

  async function connectService(serviceType: ConnectedServiceType, credentials: Record<string, string>, webhookSecret: string, capabilities: string[]) {
    setBusyFor(`connect:${serviceType}`, true);
    try {
      const response = await fetch("/api/connected-services", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          serviceType,
          credentials,
          webhookSecret: webhookSecret.trim() || undefined,
          // Every workspace agent reaches every connection; the tool gating
          // decides what each one surfaces.
          agentAccess: agents.map((a) => a.id),
          capabilities,
        }),
      });
      if (!response.ok) throw await errorFrom(response, "Failed to connect service");
      const created = (await response.json()) as ConnectedService;
      setServices((current) => [created, ...current]);
      setError(null);
      void loadStatus();
      return created;
    } catch (err) {
      fail(err);
      return null;
    } finally {
      setBusyFor(`connect:${serviceType}`, false);
    }
  }

  async function saveService(service: ConnectedService, edits: ServiceEdits): Promise<boolean> {
    setBusyFor(`save:${service.id}`, true);
    try {
      const payload: Record<string, unknown> = {
        agentAccess: service.agentAccess.length > 0 ? service.agentAccess : agents.map((a) => a.id),
        capabilities: edits.capabilities,
      };
      const credentials = filledCredentials(edits.credentials);
      if (Object.keys(credentials).length > 0) payload.credentials = credentials;
      if (edits.webhookSecret.trim().length > 0) payload.webhookSecret = edits.webhookSecret.trim();
      const response = await fetch(`/api/connected-services/${service.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw await errorFrom(response, "Failed to update connected service");
      const updated = (await response.json()) as ConnectedService;
      setServices((current) => current.map((s) => (s.id === updated.id ? updated : s)));
      setError(null);
      return true;
    } catch (err) {
      fail(err);
      return false;
    } finally {
      setBusyFor(`save:${service.id}`, false);
    }
  }

  async function removeService(serviceId: string): Promise<boolean> {
    setBusyFor(`remove:${serviceId}`, true);
    try {
      const response = await fetch(`/api/connected-services/${serviceId}`, { method: "DELETE" });
      if (!response.ok) throw await errorFrom(response, "Failed to remove connected service");
      setServices((current) => current.filter((s) => s.id !== serviceId));
      setError(null);
      void loadStatus();
      return true;
    } catch (err) {
      fail(err);
      return false;
    } finally {
      setBusyFor(`remove:${serviceId}`, false);
    }
  }

  async function refreshService(serviceId: string) {
    setBusyFor(`refresh:${serviceId}`, true);
    try {
      const response = await fetch(`/api/connected-services/${serviceId}/refresh`, { method: "POST", credentials: "include" });
      if (!response.ok) throw await errorFrom(response, "Failed to refresh connected service");
      const body = (await response.json()) as { service?: ConnectedService };
      if (body.service) {
        const refreshed = body.service;
        setServices((current) => current.map((s) => (s.id === refreshed.id ? refreshed : s)));
      }
      setError(null);
      setRepoPickerOpen(true);
      void loadStatus();
    } catch (err) {
      fail(err);
    } finally {
      setBusyFor(`refresh:${serviceId}`, false);
    }
  }

  async function retrySource(source: string) {
    setBusyFor(`retry:${source}`, true);
    try {
      const response = await fetch(`/api/settings/connector-status/${encodeURIComponent(source)}/retry`, {
        method: "POST",
        credentials: "include",
      });
      if (!response.ok) throw await errorFrom(response, `Failed to retry ${source}`);
      setRetried((current) => ({ ...current, [source]: true }));
      window.setTimeout(() => void loadStatus(), 1_500);
    } catch (err) {
      fail(err);
    } finally {
      setBusyFor(`retry:${source}`, false);
    }
  }

  async function dismissDraft(draftId: string): Promise<boolean> {
    try {
      const response = await fetch(`/api/connector-drafts/${draftId}`, { method: "DELETE" });
      if (!response.ok && response.status !== 404) throw await errorFrom(response, "Failed to dismiss connector draft");
      setDrafts((current) => current.filter((d) => d.id !== draftId));
      return true;
    } catch (err) {
      fail(err);
      return false;
    }
  }

  /** Finish a draft with its API key. Throws so the sheet can say why. */
  async function finalizeDraft(draft: ConnectorDraft, apiKey: string) {
    const response = await fetch("/api/connected-services", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ serviceType: "custom", credentials: { apiKey }, agentAccess: [], draftId: draft.id }),
    });
    if (!response.ok) throw await errorFrom(response, "Failed to connect service");
    const created = (await response.json()) as ConnectedService;
    setServices((current) => [created, ...current]);
    setDrafts((current) => current.filter((d) => d.id !== draft.id));
    void loadStatus();
    return created;
  }

  async function requestIntegration(connectorId: string) {
    setBusyFor(`request:${connectorId}`, true);
    try {
      const response = await fetch("/api/connected-services/requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ connectorId }),
      });
      if (!response.ok) throw await errorFrom(response, "Failed to request integration");
      setRequested((current) => ({ ...current, [connectorId]: true }));
    } catch (err) {
      fail(err);
    } finally {
      setBusyFor(`request:${connectorId}`, false);
    }
  }

  async function requestFreeform(label: string): Promise<boolean> {
    const trimmed = label.trim();
    if (trimmed.length < 2) return false;
    setBusyFor("freeform", true);
    try {
      const response = await fetch("/api/connected-services/requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ freeformLabel: trimmed }),
      });
      if (!response.ok) throw await errorFrom(response, "Failed to request integration");
      setFreeformDone(trimmed);
      return true;
    } catch (err) {
      fail(err);
      return false;
    } finally {
      setBusyFor("freeform", false);
    }
  }

  const records = useMemo(() => buildConnectorRecords({ services, drafts, statusRows }), [services, drafts, statusRows]);

  return {
    loading,
    error,
    statusError,
    notices,
    dismissNotice: (key: string) => setNotices((current) => current.filter((n) => n.key !== key)),
    githubPendingInstall,
    githubPendingBusy,
    dismissGithubPendingInstall: () => setGithubPendingInstall(null),
    confirmGithubPendingInstall,
    records,
    repoCount,
    loadRepoCount,
    repoPickerOpen,
    setRepoPickerOpen,
    busy,
    retried,
    requested,
    freeformDone,
    connectService,
    saveService,
    removeService,
    refreshService,
    retrySource,
    dismissDraft,
    finalizeDraft,
    requestIntegration,
    requestFreeform,
  };
}

export type Connections = ReturnType<typeof useConnections>;
